import { createContext, useEffect, useLayoutEffect, useState } from "react";
import "../Calendar.css";
import "../DatePicker.css";

import appStyles from "../App.module.css";
import { addDays, convertDateToApiFormat, totalDaysInCurrentYear } from "../app-constants";
import Popup from "./components/Popup";
import Steps from "./components/Step";
import QuoteApi from "./components/api/QuoteApi";
import BTHeader from "./components/common/BTHeader";
import LoadingModal from "./components/modal/LoadingModal";
import btConstants, { btCurrencyProductIds, hideEUHostCountries } from "./config/bt-constants";
import { IBusinessTravelContext, ITripsData, IinitialQuote } from "./config/interfaces";

function useBreakpointClass() {
  const [bpClass, setBpClass] = useState<string>("");
  useLayoutEffect(() => {
    function updateClass() {
      setBpClass(
        window.innerWidth >= 1400
          ? appStyles.bfpXXL
          : window.innerWidth >= 1200
          ? appStyles.bfpXL
          : window.innerWidth >= 992
          ? appStyles.bfpLG
          : window.innerWidth >= 768
          ? appStyles.bfpMD
          : window.innerWidth >= 576
          ? appStyles.bfpSM
          : appStyles.bfpXS
      );
    }
    window.addEventListener("resize", updateClass);
    updateClass();
    return () => window.removeEventListener("resize", updateClass);
  }, []);
  return bpClass;
}

const initialQuote: IinitialQuote = {
  host_country: null,
  currency: null,
  start_date: new Date(),
  end_date: addDays(new Date(), totalDaysInCurrentYear(new Date())),
  noOfTrips: null,
  noOfEmployee: null,
  productId: 0,
  affiliateId: btConstants?.BTaffiliatedId,
  applicants: [
    {
      is_main: true,
      dob: "1992-08-18",
      trip_cost: 1,
    },
  ],
};

export const BusinessTravelContext = createContext<IBusinessTravelContext | null>(null);

const BusinessTravel = ({ version }: any) => {
  const bpClass = useBreakpointClass();
  const totalSteps = 4;
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [quoteData, setQuoteData] = useState<IinitialQuote>(initialQuote);
  const [loader, setLoader] = useState<"quote" | "order" | "payment" | "date" | "trip" | "currency" | null>(null);

  const [order, setOrder] = useState<any>(null);
  const [quoteResult, setQuoteResult] = useState<any>(null);
  const [isPaymentInvoice, setIsPaymentInvoice] = useState<boolean>(false);
  const [tripsData, setTripsData] = useState<ITripsData | null>(null);

  useEffect(() => {
    const init = async () => {
      try {
        const _quoteData: IinitialQuote = { ...quoteData };
        const queryString = window.location.search;
        // affiliated values always available either user pass or not
        _quoteData.affiliateId = new URLSearchParams(queryString).get("partner") || btConstants.BTaffiliatedId;
        setQuoteData(_quoteData);

        const productId: any =
          version === "UK" ? btCurrencyProductIds.BTEURProductId : btCurrencyProductIds.BTEUProductId;

        const [numberOfTrips, hostCountries, destinationCountries] = await Promise.all([
          QuoteApi.getNumberOfTrips(productId),
          QuoteApi.getHostCountries(productId),
          QuoteApi.getDestinations(productId),
        ]);

        const lastTrip = numberOfTrips.data[numberOfTrips.data.length - 1];
        const newTrip = {
          id: -1,
          from: lastTrip.till + "+",
          till: lastTrip.till + "+",
          type: "trip_range",
          disabled: true,
        };

        setTripsData({
          numberOfTrips: [...numberOfTrips.data, newTrip],
          hostCountries: hostCountries.data
            ?.filter((country: any) => !country.is_disabled)
            ?.filter((country: any) => (version === "EU" ? !hideEUHostCountries.includes(country?.name) : true))
            ?.sort((a: any, b: any) => a.name.localeCompare(b.name)),
          destinationCountries: destinationCountries.data[0],
        });
      } catch (error) {
        console.error("Error occurred while initializing:", error);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // scroll to top
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  const createQuote = async (quoteData: IinitialQuote) => {
    const apiData = {
      start_date: convertDateToApiFormat(quoteData.start_date),
      end_date: convertDateToApiFormat(quoteData.end_date),
      applicants: quoteData.applicants,
      product: quoteData?.productId,
      host_country: quoteData?.host_country?.code,
      currency: quoteData.currency,
      destination_groups: [tripsData?.destinationCountries?.name],
      packages: ["base_package"],
      details: [
        {
          type: "range",
          detail: "trip_range",
          value: String(quoteData.noOfTrips?.till),
        },
      ],
      affiliate: quoteData?.affiliateId,
    };
    const createQuoteResponse = await QuoteApi.createQuote(apiData);
    if (createQuoteResponse.errors) {
      throw createQuoteResponse;
    }
    return createQuoteResponse?.data || null;
  };

  const next = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const previous = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <div className={`${appStyles.battlefacePlugin} ${bpClass}`}>
      <BusinessTravelContext.Provider
        value={{
          totalSteps,
          currentStep,
          quoteData,
          setQuoteData,
          loader,
          setLoader,
          createQuote,
          isPaymentInvoice,
          setIsPaymentInvoice,
          order,
          setOrder,
          setQuoteResult,
          quoteResult,
          tripsData,
          version,
          next,
          previous,
        }}
      >
        <BTHeader />
        {tripsData ? (
          <Steps />
        ) : (
          <Popup onClose={() => {}} title="" children={<LoadingModal />} closeButton={false} LoadingModal={true} />
        )}
      </BusinessTravelContext.Provider>
    </div>
  );
};

export default BusinessTravel;
